import { LinearGradient } from "expo-linear-gradient";
import React, { useEffect, useState } from "react";
import { Animated, AppState, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { PocLiveActivityStream } from "./PocLiveActivityStream";
import { useLoopValue, usePulseValue } from "./useAnimationHelpers";

import { colors, fonts, radius } from "@/lib/theme";

interface Props {
    // Wall-clock epoch (ms) when generation started. Phase is derived from
    // elapsed time, so background→foreground transitions resolve to the
    // correct phase instantly without dump-firing queued setTimeout calls.
    startedAt: number;
}

function pillLabelForPhase(phase: number): string {
    return phase < 2 ? "Phase 1 · Resolving Flags" : "Phase 2 · Selecting Duties";
}

export function PocGeneratingAnimation({ startedAt }: Props) {
    // 1s tick to advance the derived phase. Re-syncs immediately on AppState
    // active so foregrounding doesn't wait up to 999ms for the next tick.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const tick = () => setNow(Date.now());
        const interval = setInterval(tick, 1000);
        const sub = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                tick();
            }
        });
        return () => {
            clearInterval(interval);
            sub.remove();
        };
    }, []);

    const elapsedSec = Math.max(0, (now - startedAt) / 1000);
    const phase = elapsedSec < 10 ? 0 : elapsedSec < 30 ? 1 : elapsedSec < 70 ? 2 : 3;

    const outerSpin = useLoopValue(1000);
    const innerSpin = useLoopValue(600);
    const coreScale = usePulseValue(1400, 1, 1.6);
    const coreOpacity = usePulseValue(1400, 0.4, 0.9);
    const pillDotOpacity = usePulseValue(1200, 0.4, 1);

    const outerRotate = outerSpin.interpolate({
        inputRange: [0, 1],
        outputRange: ["0deg", "360deg"]
    });
    const innerRotate = innerSpin.interpolate({
        inputRange: [0, 1],
        outputRange: ["360deg", "0deg"]
    });

    return (
        <View style={styles.container}>
            <View style={styles.hero}>
                <View style={styles.spinnerWrapper}>
                    <Animated.View
                        style={[styles.outerRing, { transform: [{ rotate: outerRotate }] }]}
                    />
                    <Animated.View
                        style={[styles.innerRing, { transform: [{ rotate: innerRotate }] }]}
                    />
                    <Animated.View
                        style={[
                            styles.core,
                            { opacity: coreOpacity, transform: [{ scale: coreScale }] }
                        ]}
                    >
                        <LinearGradient
                            colors={[colors.teal400, colors.pink400]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.coreFill}
                        />
                    </Animated.View>
                </View>

                <LinearGradient
                    colors={[colors.teal50, colors.pink50]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.pill}
                >
                    <Animated.View style={[styles.pillDot, { opacity: pillDotOpacity }]} />
                    <Text style={styles.pillText}>{pillLabelForPhase(phase)}</Text>
                </LinearGradient>

                <Text style={styles.title}>Generating Plan of Care</Text>
                <Text style={styles.subtitle}>
                    Evaluating duty rules against the assessment data. This usually takes about a
                    minute.
                </Text>
                <Text style={styles.aside}>
                    Up to <Text style={styles.asideStrong}>90 seconds</Text> — but doing this by
                    hand would take you about 10 minutes <Text style={styles.asideWink}>🙂</Text>
                </Text>
            </View>

            <PocLiveActivityStream />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1
    },
    hero: {
        alignItems: "center",
        paddingHorizontal: 20,
        paddingTop: 32,
        paddingBottom: 16
    },
    spinnerWrapper: {
        width: 88,
        height: 88,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20
    },
    outerRing: {
        position: "absolute",
        width: 88,
        height: 88,
        borderRadius: 44,
        borderWidth: 3,
        borderColor: colors.border,
        borderTopColor: colors.teal400,
        borderRightColor: colors.pink400
    },
    innerRing: {
        position: "absolute",
        width: 64,
        height: 64,
        borderRadius: 32,
        borderWidth: 2,
        borderColor: "transparent",
        borderBottomColor: colors.pink400,
        borderLeftColor: colors.teal400
    },
    core: {
        width: 10,
        height: 10,
        borderRadius: 5,
        overflow: "hidden"
    },
    coreFill: {
        flex: 1
    },
    pill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: radius.full,
        marginBottom: 8
    },
    pillDot: {
        width: 5,
        height: 5,
        borderRadius: 2.5,
        backgroundColor: colors.teal400
    },
    pillText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.teal700,
        letterSpacing: 1.2,
        textTransform: "uppercase"
    },
    title: {
        fontSize: 22,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: -0.4,
        marginBottom: 6,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 19,
        textAlign: "center",
        maxWidth: 320,
        marginBottom: 6
    },
    aside: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        lineHeight: 18,
        textAlign: "center",
        fontStyle: "italic",
        maxWidth: 320,
        paddingHorizontal: 12
    },
    asideStrong: {
        fontFamily: fonts.bold,
        color: colors.teal600,
        fontStyle: "normal"
    },
    asideWink: {
        fontStyle: "normal"
    }
});
